import React from 'react'

import {FormikErrors, useFormik} from 'formik'

import config from '../../config'

import {useAppDispatch, useAppSelector} from '../../hooks/redux'
import {closeModal, openContactModal} from '../../redux/features/modal'

import {combineValidators, email, required} from '../../../../commons/src/libs/validator'
import {submitContactForm} from '../../../../commons/src/services/dixa'

import {ModalMeta} from '../../types'

import Button from '../../../../commons/src/components/button'
import FormRow from '../../../../commons/src/components/form-row'
import Headline from '../../../../commons/src/components/headline'
import InputField from '../../../../commons/src/components/input-field'
import Link from '../../../../commons/src/components/link'
import Modal from '../../../../commons/src/components/modal'
import Paragraph from '../../../../commons/src/components/paragraph'
import SelectField from '../../../../commons/src/components/select-field'
import SelectMenu from '../../../../commons/src/components/select-menu'
import TextField from '../../../../commons/src/components/text-field'

type Props = {
  meta: ModalMeta
}

const ContactFormModal = ({meta}: Props) => {
  const dispatch = useAppDispatch()
  const user = useAppSelector(state => state.auth.user)
  const formik = useFormik({
    initialValues: {
      name: '',
      email: user?.attributes?.email || '',
      message: '',
      sendCopy: false,
      subject: 'PartnerPortal Contact Form',
    },
    validate: values => {
      const errors: FormikErrors<typeof values> = {}

      if (!combineValidators(required, email)(values.email)) {
        errors.email = 'true'
      }

      if (!required(values.name)) {
        errors.name = 'true'
      }

      if (!values.subject) {
        errors.subject = 'true'
      }

      if (!required(values.message)) {
        errors.message = 'true'
      }

      return errors
    },
    onSubmit: async (values, {setStatus}) => {
      setStatus(undefined)
      return submitContactForm(config.dixa.formEndpoint, values)
        .then(() => setStatus('success'))
        .catch(() => setStatus('error'))
    },
  })

  return (
    <Modal
      size="l"
      fullscreenMobile={meta.fullscreenMobile}
      headline={<Headline label="Contact form" size="l" />}
      onClose={() => dispatch(closeModal())}
      buttons={[
        formik.status !== 'success' ? (
          <Button key="cancel" hierarchy="ghost" onClick={() => dispatch(closeModal())}>
            Cancel
          </Button>
        ) : undefined,
        formik.status !== 'success' ? (
          <Button
            key="contact"
            disabled={formik.isSubmitting || !formik.isValid}
            onClick={() => formik.handleSubmit()}
          >
            Submit
          </Button>
        ) : undefined,
        formik.status === 'success' ? (
          <Button key="close" onClick={() => dispatch(closeModal())}>
            Close
          </Button>
        ) : undefined,
      ]}
    >
      <Paragraph>Fill out this form and we will come back to you. </Paragraph>
      <form onSubmit={formik.handleSubmit}>
        <FormRow layout="half-half">
          <InputField
            name="name"
            placeholder="Your name"
            autoFocus
            appearance={formik.submitCount > 0 && !!formik.errors.name ? 'error' : undefined}
            onChange={(v, n, event) => formik.handleChange(event)}
            value={formik.values.name}
            onBlur={formik.handleBlur}
          />
          <InputField
            name="email"
            placeholder="Your email address"
            appearance={formik.submitCount > 0 && !!formik.errors.email ? 'error' : undefined}
            type="email"
            onChange={(v, n, event) => formik.handleChange(event)}
            value={formik.values.email}
            onBlur={formik.handleBlur}
          />
        </FormRow>
        <FormRow>
          <SelectField
            name="subject"
            placeholder="Select subject"
            appearance={formik.submitCount > 0 && !!formik.errors.subject ? 'error' : undefined}
            menu={
              <SelectMenu
                values={[
                  {value: 'Request a material or feature'},
                  {value: 'Give Feedback'},
                  {value: 'Other'},
                ]}
              />
            }
            value={formik.values.subject ? {value: formik.values.subject} : undefined}
            onChange={(val, name) => formik.setFieldValue(name || 'subject', val?.value, true)}
          />
        </FormRow>
        <FormRow>
          <TextField
            placeholder="How can we improve our service?"
            name="message"
            rows={7}
            appearance={formik.submitCount > 0 && !!formik.errors.message ? 'error' : undefined}
            onChange={(v, n, event) => formik.handleChange(event)}
            value={formik.values.message}
            onBlur={formik.handleBlur}
          />
        </FormRow>
      </form>
      {formik.status === 'error' && (
        <Paragraph error>
          At the moment we are not capable of receiving your request. Please try again later or try{' '}
          <Link onClick={() => dispatch(openContactModal({contactOptions: ['form', 'email']}))}>
            another contact method
          </Link>
          .
        </Paragraph>
      )}
      {formik.status === 'success' && (
        <Paragraph success>Thank you for contacting us. We will come back to you.</Paragraph>
      )}
    </Modal>
  )
}
export default ContactFormModal
